import React, { memo, useState } from "react";
import {
  SelectInput,
  List as BaseList,
  Datagrid,
  TextField,
  DateInput,
  DateField,
  ArrayField,
  SingleFieldList,
  ChipField,
  BooleanField,
  TopToolbar,
  FilterButton,
  CreateButton,
  usePermissions,
  useNotify,
  useDataProvider,
  SimpleForm,
  FunctionField,
  useListContext,
  useRefresh,
  useGetOne,
  useGetList,
  useInput,
} from "react-admin";
import Firebase from "../Firebase/Firebase";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ExportButton from "../components/ExcelExportButton";
import useExport from "../components/UseExcelExport";
import moment from "moment/moment";
import { SelectBranchInput } from "./SelectBranchIntput";
import UpgradePlanModal from "../components/UpgradePlanModal";
import UpgradeStickyBanner from "../utils/UpgradeStickyBanner";
import { Autocomplete, TextField as MuiTextField } from "@mui/material";

const paymentMethods = [
  { id: "Efectivo", name: "Efectivo" },
  { id: "Tarjeta", name: "Tarjeta" },
  { id: "QR", name: "QR" },
  { id: "Tigo Money", name: "Tigo Money" },
  { id: "Cortesia", name: "Cortesia" },
  { id: "Gift Card", name: "Gift Card" },
];

const status = (record) => {
  if (record.status === "Pendiente de cotizacion") {
    return [
      { id: "Pendiente de cotizacion", name: "Pendiente de cotización" },
      { id: "Confirmado", name: "Enviar cotización y confirmar" },
      // { id: "Pendiente", name: "Pendiente" },
      // { id: "Confirmado", name: "Confirmado" },
      // { id: "En proceso", name: "En proceso" },
      // { id: "Finalizado", name: "Finalizado" },
      { id: "Cancelada por el proveedor", name: "Cancelado" },
    ];
  }
  if (record.status === "Cotizacion enviada") {
    return [{ id: "Cotizacion enviada", name: "Cotización enviada" }];
  }
  if (record.status === "Pendiente") {
    return [
      { id: "Pendiente", name: "Pendiente" },
      { id: "Confirmado", name: "Confirmar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "Confirmado") {
    return [
      { id: "Confirmado", name: "Confirmado" },
      { id: "En proceso", name: "En proceso" },
      { id: "Finalizado", name: "Finalizar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "En proceso") {
    return [
      { id: "En proceso", name: "En proceso" },
      { id: "Finalizado", name: "Finalizar" },
      { id: "Cancelada por el proveedor", name: "Cancelar" },
    ];
  }
  if (record.status === "Finalizado") {
    return [{ id: "Finalizado", name: "Finalizado" }];
  }
  if (record.status === "Cancelada por el proveedor") {
    return [{ id: "Cancelada por el proveedor", name: "Cancelado por el proveedor" }];
  }
  if (record.status === "Cancelada por el cliente") {
    return [{ id: "Cancelada por el cliente", name: "Cancelado por el cliente" }];
  }
};

const ListActions = ({ filters, basePath, isFreemium, onUpgradeClick }) => {
  const { getExportData, exportData, setExportData } = useExport("booking");
  const [openModal, setOpenModal] = useState(false);
  const { filterValues } = useListContext();

  const handleExportClick = async () => {
    if (isFreemium) {
      onUpgradeClick();
      return;
    }
    if (!exportData.length) {
      setOpenModal(true);
      await getExportData(filterValues);
      setOpenModal(false);
    }
  };

  const handleClearData = () => {
    setExportData([]);
  };

  const columns = [
    { label: "ID", source: "_id" },
    {
      label: "Fecha de la creación de la reserva",
      source: "createdAt",
      transform: (val) => moment(val).format("YYYY-MM-DD"),
    },
    {
      label: "Hora de la creación de la reserva",
      source: "createdAt",
      transform: (val) => moment(val).format("HH:mm"),
    },
    { label: "Fecha de la reserva", source: "bookingDate", transform: (val) => moment(val).format("YYYY-MM-DD") },
    { label: "Hora de la reserva", source: "bookingDate", transform: (val) => moment(val).format("HH:mm") },
    { label: "Nombre del cliente", source: "customer.fullName" },
    { label: "Teléfono del cliente", source: "customer.phone" },
    {
      label: "Fecha de nacimiento",
      source: "customer.birthdate",
      transform: (val) => moment(val).format("YYYY-MM-DD"),
    },
    { label: "Género", source: "customer.gender" },
    { label: "Tipo de descuento", source: "paymentInfo.coupon.discountType" },
    { label: "Descuento", source: "paymentInfo.coupon.discount" },

    {
      label: "Dirección del cliente",
      source: "customer.address",
      transform: (address) => {
        if (!address || !address.street || !address.house || address.reference === undefined) {
          return "";
        }
        return `${address.street} #${address.house}, ${address.reference}`;
      },
    },
    { label: "Método de Reserva", source: "createdFrom" },
    { label: "Servicio en Sucursal", source: "isInBranch", transform: (isInBranch) => (isInBranch ? "Si" : "No") },
    {
      label: "Servicios Solicitados",
      source: "serviceCart",
      transform: (services) => services.map((service) => service.service.name).join(", "),
    },
    { label: "Cantidad de Servicios Solicitados", source: "serviceCart", transform: (services) => services.length },
    { label: "Precio Total", source: "paymentInfo.totalPrice" },
    { label: "Cupón de descuento", source: "coupon.name" },
    { label: "Notas", source: "notes" },
    { label: "Empleado", source: "employee.fullName" },
    { label: "Método de Pago", source: "paymentInfo.paymentMethod" },
    { label: "Estado de Reserva", source: "status" },
    { label: "Estado de Pago", source: "paymentInfo.status" },
    { label: "Sucursal", source: "branch.name" },
    { label: "Calificación", source: "feedbackInfo.rating" },
    { label: "Comentario", source: "feedbackInfo.comment" },
  ];
  const disabled = isFreemium;
  return (
    <TopToolbar
      style={{
        display: "flex",
        // justifyContent: "space-between",
        alignItems: "center",
        padding: "0 16px",
        marginBottom: "0",
      }}
    >
      <div style={{ display: "flex", gap: "16px" }}>
        <FilterButton filters={filters} disabled={disabled} onClick={disabled ? onUpgradeClick : undefined} />
        <CreateButton basePath={basePath} disabled={disabled} onClick={disabled ? onUpgradeClick : undefined} />
      </div>
      <ExportButton
        onClick={handleExportClick}
        data={exportData}
        columns={columns}
        filename="bookings.xlsx"
        clearData={handleClearData}
        disabled={disabled}
      />
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: 16 }}>
              Cargando datos para exportación...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </TopToolbar>
  );
};

export const List = (props) => {
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const profile = JSON.parse(localStorage.getItem("profile"));

  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;

  const { data: provider } = useGetOne("providers", { id: providerId });
  const { permissions } = usePermissions();
  const isFreemium = provider?.plan === "freemium";
  const BookingFilter = [
    <SelectInput
      label="Estado"
      source="status"
      disabled={isFreemium}
      choices={[
        { id: "Pendiente de cotizacion", name: "Pendiente de cotización" },
        { id: "Cotizacion enviada", name: "Cotización enviada" },
        { id: "Pendiente", name: "Pendiente" },
        { id: "Confirmado", name: "Confirmado" },
        { id: "En proceso", name: "En proceso" },
        { id: "Finalizado", name: "Finalizado" },
        { id: "Cancelada por el proveedor", name: "Cancelada por el proveedor" },
        { id: "Cancelada por el cliente", name: "Cancelada por el cliente" },
      ]}
    />,
    <DateInput label="Desde" source="bookingDate>" disabled={isFreemium} />,
    <DateInput label="Hasta" source="bookingDate<" disabled={isFreemium} />,
    <SelectInput
      label="Método"
      source="createdFrom"
      disabled={isFreemium}
      choices={[
        { id: "Manual", name: "Manual" },
        { id: "App", name: "App" },
        { id: "Colaborador", name: "Colaborador" },
        { id: "Web", name: "Web" },
      ]}
    />,
    <SelectEmployeeInput source="employee" label="Empleado" disabled={isFreemium} />,
    permissions !== "admin-sucursal" ? (
      <SelectBranchInput source="branch" label="Sucursal" alwaysOn disabled={isFreemium} />
    ) : null,
    // <SelectBranchInput source="branch" label="Sucursal" alwaysOn/>
  ].filter(Boolean);
  const showFreemiumNotice = provider?.plan === "freemium";
  return (
    <Firebase>
      <BaseList
        sort={{ field: "bookingDate", order: "DESC" }}
        filters={BookingFilter}
        {...props}
        actions={
          <ListActions
            filters={BookingFilter}
            basePath={props.basePath}
            isFreemium={provider?.plan === "freemium"}
            onUpgradeClick={() => setOpenUpgradeModal(true)}
          />
        }
      >
        {showFreemiumNotice && (
          <UpgradeStickyBanner
            planActual="Freemium"
            planDestino="Standard"
            modulo="Reservas"
            onClickUpgrade={() => setOpenUpgradeModal(true)}
          />
        )}
        <Datagrid
          rowClick={showFreemiumNotice ? false : "edit"}
          bulkActionButtons={false}
          sx={{
            pointerEvents: showFreemiumNotice ? "none" : "auto",
            opacity: showFreemiumNotice ? 0.5 : 1,
            "& .RaDatagrid-headerCell": {
              maxWidth: "100px",
              whiteSpace: "normal",
            },
            "& .RaDatagrid-cell": {
              textAlign: "center",
            },
          }}
        >
          <TextField source="_id" label="ID" />
          <DateField source="bookingDate" label="Fecha de Inicio" showTime locales="es-ES" />
          <ArrayField source="serviceCart" label="Servicios Solicitados">
            <SingleFieldList>
              <ChipField source="service.name" />
            </SingleFieldList>
          </ArrayField>
          <TextField source="paymentInfo.coupon.name" label="Cupón" />
          <TextField source="createdFrom" label="Método" />
          <TextField source="paymentInfo.totalPrice" label="Total (Bs.)" />
          <FunctionField
            source="paymentInfo.paymentMethod"
            label="Método de Pago"
            render={(record) => (
              <div onClick={(e) => e.stopPropagation()}>
                {" "}
                <PaymentStatusField record={record} />
              </div>
            )}
          />
          <FunctionField
            source="status"
            label="Estado de Reserva"
            render={(record) => (
              <div onClick={(e) => e.stopPropagation()}>
                {" "}
                <BookingStatus record={record} />
              </div>
            )}
          />
          <TextField source="paymentInfo.status" label="Estado de Pago" />
          <TextField source="feedbackInfo.rating" label="Rating" />
          <BooleanField source="isInBranch" label="¿Servicio en Sucursal?" />
          {permissions !== "admin-sucursal" && <TextField source="branch.name" label="Sucursal" />}
        </Datagrid>
      </BaseList>
      <UpgradePlanModal
        open={openUpgradeModal}
        onClose={() => setOpenUpgradeModal(false)}
        planActual={provider?.plan}
        planDestino="standard"
      />
    </Firebase>
  );
};

const PaymentStatusField = memo(({ record }) => {
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const handleChange = async (newStatus) => {
    setLoading(true);
    const totalPrice =
      newStatus === "Cortesia" ? Number(0) : record.serviceCart?.reduce((a, b) => a + (b.price || 0), 0) || 0;

    try {
      await dataProvider.update("booking", {
        id: record._id,
        data: { totalPrice: totalPrice },
        meta: { customAction: "updateTotalPrice" },
      });

      await dataProvider.update("booking", {
        id: record._id,
        data: { paymentMethod: newStatus },
        meta: { customAction: "updatePaymentMethod" },
      });
      refresh();
      notify("Actualizado correctamente", { type: "success" });
    } catch (error) {
      notify("Error al actualizar", { type: "error" });
    } finally {
      setLoading(false);
    }
  };
  return (
    <SimpleForm toolbar={false}>
      <SelectInput
        source="paymentInfo.paymentMethod"
        label="Método de Pago"
        choices={paymentMethods || []}
        disabled={loading}
        onChange={(e) => {
          return handleChange(e.target.value);
        }}
        style={{ margin: 0, padding: 0 }}
      />
    </SimpleForm>
  );
});

const BookingStatus = memo(({ record }) => {
  const [loading, setLoading] = useState(false);
  const [choices, setChoices] = useState(status(record));
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const handleCancelConfirm = async () => {
    setLoading(true);
    try {
      await dataProvider.update("booking", {
        id: record._id,
        data: { cancelReason: cancelReason.trim() }, // ✅ en data
        meta: { customAction: "toCancelledByProvider" }, // ✅ solo la acción
      });
      refresh();
      notify("Reserva cancelada con éxito", { type: "success" });
      setOpenCancelModal(false);
      setCancelReason("");
    } catch (error) {
      notify("Error al cancelar reserva", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (newStatus) => {
    if (newStatus === "Cancelada por el proveedor") {
      setOpenCancelModal(true); // Abre el modal
      return;
    }

    setLoading(true);
    try {
      await dataProvider.update("booking", {
        id: record._id,
        data: { status: newStatus, previousStatus: record.status },
        meta: { customAction: "updateStatus" },
      });

      setChoices(status({ ...record, status: newStatus }));

      notify("Actualizado correctamente", { type: "success" });
    } catch (error) {
      notify("Error al actualizar", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SimpleForm toolbar={false}>
        <SelectInput
          source="status"
          label="Estado de Reserva"
          choices={choices || []}
          disabled={loading}
          onChange={(e) => handleChange(e.target.value)}
          style={{ margin: 0, padding: 0 }}
        />
      </SimpleForm>

      {/* ✅ Modal de cancelación con motivo */}
      <Dialog open={openCancelModal} onClose={() => setOpenCancelModal(false)}>
        <DialogTitle>¿Por qué estás cancelando esta reserva?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Por favor, indica la razón. Esto ayudará al cliente a entender lo sucedido.
          </Typography>
          <MuiTextField
            autoFocus
            margin="dense"
            label="Motivo de cancelación *"
            type="text"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            required
            placeholder="Ej: Cliente no pudo asistir..."
            error={!cancelReason.trim()}
            helperText={!cancelReason.trim() ? "La razón es obligatoria" : ""}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenCancelModal(false)} color="secondary">
            Volver
          </Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained" disabled={!cancelReason.trim()}>
            Confirmar Cancelación
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
// Componente para filtro de empleados siguiendo el patrón de SelectBranchInput
export const SelectEmployeeInput = ({ source, label, disabled = false }) => {
  const { permissions } = usePermissions();
  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = permissions === "admin-sucursal" ? profile.employer._id : profile._id;
  const branchId = profile?.branch?._id;
  const branchUrl = branchId !== undefined ? `${branchId ?? ""}` : "";

  const { data: employees = [], isLoading } = useGetList("employeesAll", {
    filter: {
      providerId,
      branchUrl,
    },
  });

  const {
    field: { value, onChange },
  } = useInput({ source });

  return (
    <Box sx={{ mb: 1 }}>
      <Autocomplete
        size="small"
        disabled={disabled}
        options={employees}
        getOptionLabel={(option) => (option ? `${option.first_name} ${option.last_name}` : "")}
        value={employees.find((employee) => employee._id === value) || null}
        onChange={(event, newValue) => onChange(newValue ? newValue._id : "")}
        renderInput={(params) => (
          <MuiTextField {...params} label={label || "Empleado"} variant="outlined" disabled={isLoading || disabled} />
        )}
        sx={{ width: 200 }}
      />
    </Box>
  );
};
